import { ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "../../../hooks/useTranslation";
import { ADMIN_ROLES } from "../../../utils/adminAccess";
import { CopyIconButton } from "./CopyIconButton";

type Parsed = {
  sub?: string;
  email?: string;
  preferred_username?: string;
  exp?: number;
  realm_access?: { roles?: string[] };
};

// "label   value" with the labels lined up
const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "150px minmax(0, 1fr)",
      columnGap: 1.5,
      alignItems: "center",
      minHeight: 30,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

const Mono = ({ children }: { children: ReactNode }) => (
  <Typography
    component="span"
    variant="body2"
    sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
  >
    {children}
  </Typography>
);

type Props = { open: boolean; onClose: () => void };

// Who the panel is talking to the backend as (developer tool, test
// environment only): the Keycloak user, every admin role it has or lacks
// with what that role opens, when the token ends, and the token itself to
// try the same calls in Swagger.
export const SessionDialog = ({ open, onClose }: Props) => {
  const { translate } = useTranslation("pages.admin.header.session");
  const { keycloak } = useKeycloak();
  const parsed = (keycloak.tokenParsed ?? {}) as Parsed;
  const roles = parsed.realm_access?.roles ?? [];
  const expires = parsed.exp ? new Date(parsed.exp * 1000) : null;
  const minutesLeft = expires
    ? Math.max(0, Math.round((expires.getTime() - Date.now()) / 60_000))
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>{translate("title")}</DialogTitle>
      <DialogContent>
        <Stack gap={2.5} sx={{ pt: 1 }}>
          <Stack gap={0.25}>
            <Row label={translate("userId")}>
              {parsed.sub ? (
                <Stack direction="row" alignItems="center" gap={0.25}>
                  <Mono>{parsed.sub}</Mono>
                  <CopyIconButton value={parsed.sub} />
                </Stack>
              ) : (
                "-"
              )}
            </Row>
            <Row label={translate("email")}>
              <Typography variant="body2">{parsed.email ?? "-"}</Typography>
            </Row>
            <Row label={translate("username")}>
              <Typography variant="body2">
                {parsed.preferred_username ?? "-"}
              </Typography>
            </Row>
            <Row label={translate("expires")}>
              <Typography variant="body2">
                {expires
                  ? translate("expiresAt", {
                      time: expires.toISOString().slice(11, 16),
                      minutes: minutesLeft ?? 0,
                    })
                  : "-"}
              </Typography>
            </Row>
          </Stack>

          <Stack gap={1}>
            <Typography
              variant="caption"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: "text.secondary",
              }}
            >
              {translate("rolesTitle")}
            </Typography>
            <Stack gap={0.75}>
              {Object.values(ADMIN_ROLES).map((role) => {
                const has = roles.includes(role);
                const Icon = has ? CheckCircleOutline : RemoveCircleOutline;
                return (
                  <Stack
                    key={role}
                    direction="row"
                    gap={1}
                    alignItems="flex-start"
                  >
                    <Icon
                      sx={{
                        fontSize: 18,
                        mt: 0.15,
                        color: has ? "success.main" : "text.disabled",
                      }}
                    />
                    <Stack sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          color: has ? "text.primary" : "text.secondary",
                        }}
                      >
                        {role}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {translate(`roles.${role}`)}
                      </Typography>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {translate("tokenHint")}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {keycloak.token && (
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ mr: "auto" }}
          >
            <CopyIconButton
              value={keycloak.token}
              label={translate("copyToken")}
            />
            <Typography variant="body2" color="text.secondary">
              {translate("copyToken")}
            </Typography>
          </Stack>
        )}
        <Button onClick={onClose}>{translate("close")}</Button>
      </DialogActions>
    </Dialog>
  );
};
